export interface LanguageOption {
  code: string;
  label: string;
  shortCode: string; // ISO-style 2-letter code for UI display
  isBeta?: boolean; // Beta languages only assignable by admin
}

export const ALL_LANGUAGES: LanguageOption[] = [
  { code: 'english', label: 'English', shortCode: 'EN' },
  { code: 'spanish', label: 'Spanish (Spain)', shortCode: 'ES' },
  { code: 'spanish-latam', label: 'Spanish (Latin America)', shortCode: 'ES' },
  { code: 'italian', label: 'Italian', shortCode: 'IT' },
  { code: 'dutch', label: 'Dutch', shortCode: 'NL' },
  { code: 'portuguese', label: 'Portuguese', shortCode: 'PT' },
  { code: 'french', label: 'French', shortCode: 'FR' },
  { code: 'german', label: 'German', shortCode: 'DE' },
  { code: 'catalan', label: 'Catalan', shortCode: 'CA' },
];

// Public languages for user-facing selectors (excludes beta)
export const PUBLIC_LANGUAGES = ALL_LANGUAGES.filter(l => !l.isBeta);

// Native language options: all public languages (English is already in ALL_LANGUAGES)
export const NATIVE_LANGUAGE_OPTIONS: LanguageOption[] = PUBLIC_LANGUAGES;

// Returns available languages for a user
// If user already has a beta language, include it in their options
export const getAvailableLanguages = (currentLanguage?: string): LanguageOption[] => {
  if (currentLanguage) {
    const currentLang = ALL_LANGUAGES.find(l => l.code === currentLanguage);
    if (currentLang?.isBeta) {
      return [...PUBLIC_LANGUAGES, currentLang];
    }
  }
  return PUBLIC_LANGUAGES;
};

// Helper to get short code from language code
export const getLanguageShortCode = (languageCode: string): string => {
  const lang = ALL_LANGUAGES.find(l => l.code === languageCode)
    || NATIVE_LANGUAGE_OPTIONS.find(l => l.code === languageCode);
  return lang?.shortCode || '??';
};

// Helper to get flag emoji from language code
export const getLanguageFlag = (languageCode: string): string => {
  const flagMap: Record<string, string> = {
    'english': '🇬🇧',
    'spanish': '🇪🇸',
    'spanish-latam': '🇲🇽',
    'italian': '🇮🇹',
    'dutch': '🇳🇱',
    'portuguese': '🇵🇹',
    'french': '🇫🇷',
    'german': '🇩🇪',
    'catalan': '🇪🇸',
  };
  return flagMap[languageCode] || '🌐';
};

// Helper to get display name from language code (TD-001: Centralized source of truth)
export const getLanguageDisplayName = (languageCode: string): string => {
  const displayNames: Record<string, string> = {
    'english': 'English',
    'spanish': 'Spanish',
    'spanish-latam': 'Spanish (Latin America)',
    'italian': 'Italian',
    'dutch': 'Dutch',
    'portuguese': 'Portuguese',
    'french': 'French',
    'german': 'German',
    'catalan': 'Catalan',
  };
  return displayNames[languageCode] || languageCode.charAt(0).toUpperCase() + languageCode.slice(1);
};

// Simple display name (without region info) for UI labels
export const getLanguageDisplayNameSimple = (languageCode: string): string => {
  const simpleNames: Record<string, string> = {
    'english': 'English',
    'spanish': 'Spanish',
    'spanish-latam': 'Spanish',
    'italian': 'Italian',
    'dutch': 'Dutch',
    'portuguese': 'Portuguese',
    'french': 'French',
    'german': 'German',
    'catalan': 'Catalan',
  };
  return simpleNames[languageCode] || languageCode.charAt(0).toUpperCase() + languageCode.slice(1);
};

